import type {
  ApprovalStatus,
  CalendarChannel,
  CampaignStatus,
  EventStatus,
  NotificationType,
  PostEtapa,
  TaskFrente,
  TaskPilar,
  TaskPrioridade,
  TaskStatus,
  TaskTipo,
  UserRole,
} from '@/types/database'

export interface Option<T extends string> {
  value: T
  label: string
  /** classe utilitária opcional para "badge" (dot/bg). */
  tone?: string
  description?: string
}

// --- Frentes / Ambientes (7, cores reais em src/lib/ambientes.ts) -----------
export const FRENTES: Option<TaskFrente>[] = [
  { value: 'GERAL', label: 'Geral' },
  { value: 'CAR', label: 'CAR' },
  { value: 'BIKE', label: 'BIKE' },
  { value: 'TRUCK', label: 'TRUCK' },
  { value: 'OHW', label: 'OHW' },
  { value: 'KONFORT', label: 'KONFORT' },
  { value: 'MARINE', label: 'MARINE' },
  { value: 'TEXAEDU', label: 'TEXAEDU' },
]

// --- Formato do conteúdo (texto canônico) ----------------------------------
export const FORMATOS = ['Vídeo', 'Carrossel', 'Imagem', 'Reel', 'Story', 'Outro'] as const

// --- Canais ----------------------------------------------------------------
export const CANAIS = [
  'Instagram',
  'YouTube',
  'TikTok',
  'Meta Ads',
  'LinkedIn',
  'Facebook',
] as const

// --- Estágios do pipeline de produção --------------------------------------
export const PRODUCTION_STAGES = [
  { value: 'roteiro', label: 'Roteiro' },
  { value: 'gravacao', label: 'Gravação' },
  { value: 'postagem', label: 'Postagem' },
] as const

export const STAGE_STATUS: { value: 'pendente' | 'em_andamento' | 'concluido'; label: string }[] = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluido', label: 'Concluído' },
]

// --- Tipos -----------------------------------------------------------------
export const TIPOS: Option<TaskTipo>[] = [
  { value: 'planejamento', label: 'Planejamento' },
  { value: 'organico', label: 'Orgânico' },
  { value: 'campanha', label: 'Campanha' },
  { value: 'evento', label: 'Evento' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'copy_revisao', label: 'Copy / revisão' },
  { value: 'landing_page', label: 'Landing page' },
  { value: 'video', label: 'Vídeo' },
  { value: 'carrossel', label: 'Carrossel' },
  { value: 'post_estatico', label: 'Post estático' },
]

// --- Pilares ---------------------------------------------------------------
export const PILARES: Option<TaskPilar>[] = [
  { value: 'educacao_tecnica', label: 'Educação técnica' },
  { value: 'produto', label: 'Produto' },
  { value: 'aplicacao', label: 'Aplicação' },
  { value: 'duvida', label: 'Dúvida' },
  { value: 'bastidor', label: 'Bastidor' },
  { value: 'evento', label: 'Evento' },
  { value: 'caso', label: 'Caso' },
  { value: 'parceiro', label: 'Parceiro' },
]

// --- Status (ordem = colunas do Kanban) ------------------------------------
export const STATUSES: Option<TaskStatus>[] = [
  { value: 'entrada', label: 'Entrada', description: 'Chegou, ainda não triado.' },
  {
    value: 'aguardando_briefing',
    label: 'Aguardando briefing',
    description: 'Falta informação para começar.',
  },
  {
    value: 'pronto_para_produzir',
    label: 'Pronto para produzir',
    description: 'Briefing completo.',
  },
  { value: 'em_producao', label: 'Em produção', description: 'Sendo feito.' },
  {
    value: 'aguardando_material',
    label: 'Aguardando material',
    description: 'Depende de terceiro/insumo.',
  },
  {
    value: 'aguardando_aprovacao',
    label: 'Aguardando aprovação',
    description: 'Enviado para aprovar.',
  },
  { value: 'em_ajustes', label: 'Em ajustes', description: 'Voltou com correções.' },
  { value: 'agendado', label: 'Agendado', description: 'Pronto e datado.' },
  { value: 'publicado', label: 'Publicado / entregue', description: 'Concluído.' },
  { value: 'backlog_editorial', label: 'Backlog editorial', description: 'Ideias para depois.' },
]

export const KANBAN_ORDER: TaskStatus[] = STATUSES.map((s) => s.value)

// --- Etapas do post (pipeline confirmado: o ciclo de vida do conteúdo) ------
export const ETAPAS: Option<PostEtapa>[] = [
  { value: 'ideia', label: 'Ideia' },
  { value: 'roteiro', label: 'Roteiro' },
  { value: 'gravacao', label: 'Gravação' },
  { value: 'edicao', label: 'Edição' },
  { value: 'aprovacao', label: 'Aprovação' },
  { value: 'agendado', label: 'Agendado' },
  { value: 'publicado', label: 'Publicado' },
]
export const ETAPA_ORDER: PostEtapa[] = ETAPAS.map((e) => e.value)

/** Fases que agrupam as etapas no board. */
export const ETAPA_PHASES: { title: string; etapas: PostEtapa[] }[] = [
  { title: 'Planejar', etapas: ['ideia'] },
  { title: 'Produzir', etapas: ['roteiro', 'gravacao', 'edicao'] },
  { title: 'Aprovar', etapas: ['aprovacao'] },
  { title: 'Publicar', etapas: ['agendado', 'publicado'] },
]

/** Cor por etapa (tom translúcido para badges). */
export const ETAPA_TONE: Record<PostEtapa, string> = {
  ideia: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
  roteiro: 'bg-sky-500/15 text-sky-600 dark:text-sky-300',
  gravacao: 'bg-blue-500/15 text-blue-600 dark:text-blue-300',
  edicao: 'bg-violet-500/15 text-violet-600 dark:text-violet-300',
  aprovacao: 'bg-amber-500/15 text-amber-600 dark:text-amber-300',
  agendado: 'bg-teal-500/15 text-teal-600 dark:text-teal-300',
  publicado: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
}

/** Fases do ciclo de vida do conteúdo (agrupam as colunas do board). */
export const KANBAN_PHASES: { title: string; statuses: TaskStatus[] }[] = [
  { title: 'Planejar', statuses: ['entrada', 'aguardando_briefing', 'backlog_editorial'] },
  { title: 'Produzir', statuses: ['pronto_para_produzir', 'em_producao', 'aguardando_material'] },
  { title: 'Revisar', statuses: ['aguardando_aprovacao', 'em_ajustes'] },
  { title: 'Publicar', statuses: ['agendado', 'publicado'] },
]

// --- Prioridade ------------------------------------------------------------
export const PRIORIDADES: (Option<TaskPrioridade> & { weight: number })[] = [
  { value: 'critica', label: 'Crítica', weight: 0, tone: 'text-destructive' },
  { value: 'alta', label: 'Alta', weight: 1, tone: 'text-warning' },
  { value: 'planejada', label: 'Planejada', weight: 2, tone: 'text-primary' },
  { value: 'backlog', label: 'Backlog', weight: 3, tone: 'text-muted-foreground' },
]

// --- Aprovação -------------------------------------------------------------
export const APPROVAL_STATUSES: Option<ApprovalStatus>[] = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'reprovado', label: 'Reprovado' },
  { value: 'ajustes_solicitados', label: 'Ajustes solicitados' },
]

// --- Campanha / Evento -----------------------------------------------------
export const CAMPAIGN_STATUSES: Option<CampaignStatus>[] = [
  { value: 'planejamento', label: 'Planejamento' },
  { value: 'ativa', label: 'Ativa' },
  { value: 'pausada', label: 'Pausada' },
  { value: 'concluida', label: 'Concluída' },
  { value: 'cancelada', label: 'Cancelada' },
]

export const EVENT_STATUSES: Option<EventStatus>[] = [
  { value: 'planejamento', label: 'Planejamento' },
  { value: 'confirmado', label: 'Confirmado' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'cancelado', label: 'Cancelado' },
]

// --- Canais do calendário --------------------------------------------------
export const CALENDAR_CHANNELS: Option<CalendarChannel>[] = [
  { value: 'instagram_feed', label: 'Instagram Feed' },
  { value: 'instagram_stories', label: 'Instagram Stories' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'blog', label: 'Blog' },
  { value: 'campanha', label: 'Campanha' },
  { value: 'evento', label: 'Evento' },
]

// --- Papéis ----------------------------------------------------------------
export const ROLES: Option<UserRole>[] = [
  { value: 'admin', label: 'Admin', description: 'Controle total do workspace.' },
  { value: 'gestor', label: 'Gestor', description: 'Gerencia conteúdo e estrutura.' },
  { value: 'colaborador', label: 'Colaborador', description: 'Cria e edita tarefas.' },
  { value: 'aprovador', label: 'Aprovador', description: 'Aprova ou reprova entregas.' },
  { value: 'visualizador', label: 'Visualizador', description: 'Somente leitura.' },
]

// --- Tipos de notificação --------------------------------------------------
export const NOTIFICATION_LABELS: Record<NotificationType, string> = {
  mencao: 'Menção',
  atribuicao: 'Atribuição',
  comentario: 'Comentário',
  aprovacao_solicitada: 'Aprovação solicitada',
  aprovacao_resultado: 'Resultado de aprovação',
  mudanca_status: 'Mudança de status',
  prazo_proximo: 'Prazo próximo',
  prazo_atrasado: 'Prazo atrasado',
}

// --- Mapas de rótulo (value → label) ---------------------------------------
function toMap<T extends string>(opts: Option<T>[]): Record<T, string> {
  return Object.fromEntries(opts.map((o) => [o.value, o.label])) as Record<T, string>
}

export const LABELS = {
  frente: toMap(FRENTES),
  tipo: toMap(TIPOS),
  pilar: toMap(PILARES),
  status: toMap(STATUSES),
  etapa: toMap(ETAPAS),
  prioridade: toMap(PRIORIDADES),
  approval: toMap(APPROVAL_STATUSES),
  campaign: toMap(CAMPAIGN_STATUSES),
  event: toMap(EVENT_STATUSES),
  channel: toMap(CALENDAR_CHANNELS),
  role: toMap(ROLES),
}

// --- Permissões (espelham a RLS; a checagem final é sempre no banco) --------
export const WRITER_ROLES: UserRole[] = ['admin', 'gestor', 'colaborador']
export const MANAGER_ROLES: UserRole[] = ['admin', 'gestor']
export const APPROVER_ROLES: UserRole[] = ['admin', 'gestor', 'aprovador']

export const STORAGE_LIMITS = {
  attachmentBytes: 50 * 1024 * 1024,
  avatarBytes: 2 * 1024 * 1024,
  attachmentMime: [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'image/svg+xml',
    'application/pdf',
    'video/mp4',
    'video/quicktime',
    'application/zip',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
  ],
  avatarMime: ['image/png', 'image/jpeg', 'image/webp'],
}
